import { useCallback, useEffect, useRef, useState } from 'react';

import { useDebouncedCallback } from '../../../../common/utils/use-debounced-callback';

import type { IntentMode } from './types';

export type UseIntentRoutingArgs = {
	/**
	 * Current query text used for intent detection.
	 */
	query: string;
	/**
	 * Debounce delay for intent detection in milliseconds.
	 */
	debounceMs: number;
	/**
	 * Async intent detection function for the query.
	 */
	intentDetection: (query: string) => Promise<IntentMode>;
	/**
	 * Minimum query length required to run intent detection.
	 */
	minQueryLength?: number;
	/**
	 * When true, intent detection and swapping are disabled and the primary mode is pinned.
	 */
	disabled?: boolean;
	/**
	 * Used when disabled is true.
	 */
	initialPrimaryMode?: IntentMode;
};

export type UseIntentRoutingResult = {
	/**
	 * Current primary intent mode used to order the intent rows.
	 */
	primaryMode: IntentMode;
	/**
	 * Whether intent detection is paused (e.g. during keyboard navigation).
	 */
	isPaused: boolean;
	/**
	 * Pause intent detection.
	 */
	pause: () => void;
	/**
	 * Resume intent detection.
	 */
	resume: () => void;
	/** Pause due to keyboard navigation highlighting. */
	onKeyboardHighlight: () => void;
};

/**
 * Debounced intent routing with pause/resume.
 */
export const useIntentRouting = ({
	query,
	debounceMs,
	intentDetection,
	minQueryLength = 2,
	disabled = false,
	initialPrimaryMode = 'ALL_APPS',
}: UseIntentRoutingArgs): UseIntentRoutingResult => {
	const [primaryMode, setPrimaryMode] = useState<IntentMode>(initialPrimaryMode);
	const [isPaused, setIsPaused] = useState(false);
	const keyboardResumeTimeoutRef = useRef<number | null>(null);

	// Pin primary mode whenever we are disabled.
	useEffect(() => {
		if (disabled) {
			setPrimaryMode(initialPrimaryMode);
		}
	}, [disabled, initialPrimaryMode]);

	const shouldRun = !disabled && query.length >= minQueryLength && !isPaused;

	const runDetection = useCallback(async () => {
		if (!shouldRun) {
			return;
		}
		try {
			const mode = await intentDetection(query);
			setPrimaryMode(mode);
		} catch {
			// Swallow errors for now; Milestone 5 will add real error handling/telemetry.
		}
	}, [intentDetection, query, shouldRun]);

	const debouncedDetect = useDebouncedCallback(runDetection, debounceMs);

	useEffect(() => {
		if (!shouldRun) {
			return;
		}
		debouncedDetect();
		return () => {
			debouncedDetect.cancel();
		};
	}, [debouncedDetect, shouldRun, query]);

	const pause = useCallback(() => {
		setIsPaused(true);
		debouncedDetect.cancel();
	}, [debouncedDetect]);

	const resume = useCallback(() => {
		setIsPaused(false);
	}, []);

	// Pause intent detection while navigating, then resume after a short idle.
	const onKeyboardHighlight = useCallback(() => {
		pause();
		if (keyboardResumeTimeoutRef.current) {
			window.clearTimeout(keyboardResumeTimeoutRef.current);
		}
		// Resume after a short idle window (user stops navigating).
		keyboardResumeTimeoutRef.current = window.setTimeout(() => {
			setIsPaused(false);
		}, 500);
	}, [pause]);

	useEffect(() => {
		return () => {
			if (keyboardResumeTimeoutRef.current) {
				window.clearTimeout(keyboardResumeTimeoutRef.current);
			}
		};
	}, []);

	return { primaryMode, isPaused, pause, resume, onKeyboardHighlight };
};
